import { useQuery } from '@tanstack/react-query';
import { type LocationDescriptor } from 'history';
import { useEffect, useRef } from 'react';

import {
  AuthSessionStatus,
  api,
  getQueryProps,
  isDefined,
  useAuthExpirationContext,
  useLoggedInAuthContext,
} from 'common';

import notificationAudio from './assets/notificationAudio.wav';
import { LeaderTabStatus, useBrowserNotificationsContext } from './useBrowserNotifications.js';
import { getPathFromLocationDescriptor } from '../../utils/navigation.js';

import notableIcon from './assets/notableIcon.svg';

const DEFAULT_POLL_INTERVAL_MS = 15_000 as const;

let cachedNotificationAudio: HTMLAudioElement | null = null;

function parseTimestampMilliseconds(value: string | null): number | null {
  if (!isDefined(value)) {
    return null;
  }
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) ? milliseconds : null;
}

function buildAgentConversationLocationDescriptor(
  notification: api.BrowserNotification.AgentConversationBrowserNotification
): LocationDescriptor {
  return {
    pathname: `/entity/patients/${notification.recipientEhrInformationId}`,
    search: `tab=agentConversation&agentConversationId=${notification.id}`,
  };
}

function emitDesktopNotification(params: {
  title: string;
  body: string;
  locationDescriptor: LocationDescriptor;
}) {
  const { title, body, locationDescriptor } = params;
  const url = getPathFromLocationDescriptor(locationDescriptor);
  const notification = new Notification(title, {
    body,
    data: { url },
    icon: notableIcon,
  });

  tryPlayNotificationAudio();

  notification.onclick = (event) => {
    event.preventDefault();
    try {
      notification.close();
    } catch (error) {
      console.error('Error closing notification:', error);
    }
    window.open(`#${url}`, '_blank');
  };
}

function tryPlayNotificationAudio() {
  try {
    if (cachedNotificationAudio?.src !== notificationAudio) {
      cachedNotificationAudio = new Audio(notificationAudio);
      cachedNotificationAudio.preload = 'none';
    }
    cachedNotificationAudio.currentTime = 0;

    cachedNotificationAudio.play()?.catch((error) => {
      console.error('Error playing notification audio', error);
    });
  } catch (error) {
    // this is a fallback for when the audio file is not found or the user has disabled notifications
    console.error('Error playing notification audio', error);
  }
}

/**
 * This hook polls for new notifications and emits desktop notifications.
 *
 * The notifications processing `useEffect` in this hook depends on:
 * - `canProcessNotificationResults` (derived from permissions, leader tab status, feature flags, and query state),
 * - `notificationsResults.data.data` (the latest batch of notifications),
 * - `notificationTitle` / `notificationBody` (feature flag values),
 * - `memoizedSetWatermarkMilliseconds` (context setter used when advancing the watermark).
 *
 * The `useQuery` call also depends on `watermarkMilliseconds` because it is used to build
 * the `afterTimestamp` parameter, so changes to the watermark change the query inputs.
 *
 * Circular logic:
 *  - the effect advances the watermark via `memoizedSetWatermarkMilliseconds`
 *  - this updates `watermarkMilliseconds` and therefore the query's `afterTimestamp`.
 *  - this can change `notificationsResults.data.data`, re-triggering the effect.
 *     - The effect relies on `watermarkMillisecondsRef` and per-conversation tracking to avoid re-notifying on the same items while still allowing new activity to notify.
 */
export function useBrowserNotificationsPoller() {
  const {
    canEmitNotifications,
    leaderTabStatus,
    watermarkMilliseconds,
    memoizedSetWatermarkMilliseconds,
  } = useBrowserNotificationsContext();
  const { permissions, isAdminRole, isStaffRole } = useLoggedInAuthContext();
  const { sessionStatus } = useAuthExpirationContext();

  // Track the last timestamp we notified for *each* conversation id, so we can notify again
  // if the same conversation gets new activity later.
  // (We still use `watermarkMs` as a baseline to avoid notifying on backlog after refresh.)
  const lastNotifiedMillisecondsByConversationIdRef = useRef<Map<string, number>>(new Map());

  const notificationsResults = useQuery({
    ...getQueryProps(api.client.Notifications, 'findAll', {
      afterTimestamp: new Date(watermarkMilliseconds).toISOString(),
      page: { size: 100 },
    }),
    enabled:
      sessionStatus !== AuthSessionStatus.Expired &&
      leaderTabStatus === LeaderTabStatus.Leader &&
      canEmitNotifications &&
      (isAdminRole || isStaffRole) &&
      permissions.includes(api.Group.Permission.ModifyAgentConversation),
    refetchInterval: DEFAULT_POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });

  const watermarkMillisecondsRef = useRef(watermarkMilliseconds);

  useEffect(() => {
    watermarkMillisecondsRef.current = watermarkMilliseconds;
  }, [watermarkMilliseconds]);

  const canProcessNotificationResults =
    leaderTabStatus === LeaderTabStatus.Leader &&
    canEmitNotifications &&
    !notificationsResults.isPending &&
    !notificationsResults.isError &&
    notificationsResults.data.data.length > 0;

  /**
   * This processes each polling result and emits desktop notifications for newly-updated conversations.
   *
   * It uses `watermarkMillisecondsRef` and `lastNotifiedMillisecondsByConversationIdRef` to avoid
   * re-notifying on the same events while still allowing the same conversation to notify again if
   * it receives new activity later.
   *
   * When we successfully notify, we advance the watermark (both the ref and persisted storage) to the
   * max event timestamp seen, which updates the query `afterTimestamp` to only fetch newer events.
   *
   * `useEffect` review
   * Guidelines: https://notable-health.atlassian.net/wiki/spaces/notableengineering/pages/2566389961/React+State+Management#Required-Review
   *
   * This has been reviewed by a UI tech lead to be safe.
   * It runs only when processing is allowed and when the latest query data / notification copy changes.
   */
  useEffect(() => {
    if (!canProcessNotificationResults) {
      return;
    }

    const lastNotifiedMillisecondsByConversationId =
      lastNotifiedMillisecondsByConversationIdRef.current;

    let maxTimestampMilliseconds = watermarkMillisecondsRef.current;
    const conversationNotifications = notificationsResults.data.data;
    for (const conversationNotification of conversationNotifications) {
      const eventTimestampMilliseconds =
        parseTimestampMilliseconds(conversationNotification.lastRecipientRepliedTimestamp) ??
        parseTimestampMilliseconds(conversationNotification.created);
      if (!isDefined(eventTimestampMilliseconds)) {
        continue;
      }

      const lastNotifiedForConversationMilliseconds =
        lastNotifiedMillisecondsByConversationId.get(conversationNotification.id) ??
        watermarkMillisecondsRef.current;
      if (eventTimestampMilliseconds <= lastNotifiedForConversationMilliseconds) {
        continue;
      }

      const agentConversationLocationDescriptor =
        buildAgentConversationLocationDescriptor(conversationNotification);

      emitDesktopNotification({
        title: 'New Conversation Task',
        body: 'A conversation requires assistance.',
        locationDescriptor: agentConversationLocationDescriptor,
      });

      lastNotifiedMillisecondsByConversationId.set(
        conversationNotification.id,
        eventTimestampMilliseconds
      );
      maxTimestampMilliseconds = Math.max(maxTimestampMilliseconds, eventTimestampMilliseconds);
    }

    if (maxTimestampMilliseconds !== watermarkMillisecondsRef.current) {
      watermarkMillisecondsRef.current = maxTimestampMilliseconds;
      memoizedSetWatermarkMilliseconds(maxTimestampMilliseconds);
    }
  }, [
    canProcessNotificationResults,
    notificationsResults.data?.data,
    memoizedSetWatermarkMilliseconds,
  ]);
}
